import { TicketData, PrinterProfile } from '../types/printerProfile';
import {
  aggregateCustomizationOptions,
} from './ticketFormatter';
import { groupItemsByCategory } from './categoryGrouper';
import { buildAdvancedTicket } from './ticketFormatterAdvanced';
import { formatOrderNumber } from './orderNumberFormatter';

export const buildModernTicket = (data: TicketData, profile?: PrinterProfile): string => {
  if (profile?.config?.advancedTemplate) {
    return buildAdvancedTicket(data, profile);
  }
  const {
    storeInfo,
    orderCode,
    items,
    categories = [],
    orderType,
    tableNumber,
    customerName,
    taxResult,
    orderTotals,
    paymentMethod,
    profileSettings,
    taxSettings,
    t,
    loyaltyDiscount = 0,
    loyaltyPointsUsed = 0,
    happyHourDiscount = 0,
    happyHourPercent = 0,
  } = data;

  const config = profile?.config || {};
  const width = config.width || 42;
  const paddingWidth = config.paddingWidth || 20;
  const separator = config.separator || '------------------------------------------';
  const useEscPos = config.useEscPos !== false;
  const showCategories = config.showCategories !== false && taxSettings?.showCategories;
  const showTaxDetails = config.showTaxDetails !== false;
  const aggregateOpts = config.aggregateOptions !== false;
  const formatNotes = config.formatNotes !== false;

  const centerText = (text: string): string => {
    const padding = Math.max(0, Math.floor((width - text.length) / 2));
    return ' '.repeat(padding) + text;
  };

  const formatLine = (left: string, right: string): string => {
    const space = Math.max(2, width - left.length - right.length);
    return left + ' '.repeat(space) + right;
  };

  const formatQuantityItem = (qty: number, name: string, total: number, currency: string): string => {
    const itemStr = `${qty}x ${name}`;
    const priceStr = `${total.toFixed(2)}${currency}`;
    const availableSpace = width - itemStr.length - priceStr.length - 2;
    if (availableSpace < 0) {
      return `${itemStr}\n${' '.repeat(width - priceStr.length)}${priceStr}`;
    }
    return `${itemStr}${' '.repeat(Math.max(2, availableSpace))}${priceStr}`;
  };

  const formatCustomization = (name: string, price: number | null, currency: string, quantity?: number): string => {
    const qtyStr = quantity && quantity > 1 ? ` x${quantity}` : '';
    if (price === null || price === 0) {
      return `  + ${name}${qtyStr}`;
    }
    const priceStr = `+${price.toFixed(2)}${currency}`;
    const optStr = `  + ${name}${qtyStr}`;
    const space = Math.max(1, width - optStr.length - priceStr.length);
    return `${optStr}${' '.repeat(space)}${priceStr}`;
  };

  const formatItemNote = (note: string): string[] => {
    const prefix = '  Note: ';
    const maxWidth = width - prefix.length;
    const words = note.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if ((currentLine + word).length <= maxWidth) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(prefix + currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(prefix + currentLine);
    return lines;
  };

  const lines: string[] = [];
  const currency = 'EUR';
  const dateStr = new Date().toLocaleString('fr-FR');
  const orderTypeStr = orderType === 'takeaway' ? t('À emporter') : t('Sur place');
  const tableStr = tableNumber ? ` - ${t('Table')} ${tableNumber}` : '';
  const terminalName = profileSettings?.terminalName || 'Borne 1';

  lines.push('[HEADER:START]');
  if (useEscPos) {
    lines.push('\x1B\x21\x18');
  }
  if (storeInfo?.name) {
    lines.push(centerText(storeInfo.name));
  }
  lines.push(separator);
  lines.push(centerText(`**** #${formatOrderNumber(orderCode)} ****`));
  lines.push(centerText(terminalName));
  lines.push(separator);
  if (useEscPos) {
    lines.push('\x1B\x21\x00');
  }
  lines.push(formatLine(`${t('Date')}:`, dateStr));
  lines.push(formatLine(`${t('Type')}:`, `${orderTypeStr}${tableStr}`));
  if (customerName) {
    lines.push(formatLine(`${t('Client')}:`, customerName));
  }
  lines.push('[HEADER:END]');

  lines.push(separator);
  lines.push('[ITEMS:START]');

  // Toujours grouper par catégorie pour le routage
  if (categories.length > 0) {
    const groupedItems = groupItemsByCategory(items, categories);
    const safeCategoryName = (name: string) => name.replace(/[|\]:]/g, '_');

    for (let i = 0; i < groupedItems.length; i++) {
      const group = groupedItems[i];
      const catName = safeCategoryName(group.categoryName);

      // TOUJOURS ajouter les marqueurs pour le routage
      lines.push(`[CAT:${group.categoryId}|${catName}:START]`);

      // Afficher l'en-tête SEULEMENT si showCategories est activé
      if (showCategories) {
        lines.push(centerText(`-- ${group.categoryName} --`));
      }

      for (let itemIdx = 0; itemIdx < group.items.length; itemIdx++) {
        const item = group.items[itemIdx];
        const itemTotal = item.menuItem.price * item.quantity;
        lines.push(formatQuantityItem(item.quantity, item.menuItem.name, itemTotal, currency));

        if (aggregateOpts) {
          for (const [, options] of Object.entries(item.customizations)) {
            const aggregated = aggregateCustomizationOptions(options);
            for (const opt of aggregated) {
              lines.push(formatCustomization(opt.name, opt.price > 0 ? opt.price : null, currency, opt.quantity));
            }
          }
        } else {
          for (const [, options] of Object.entries(item.customizations)) {
            for (const opt of options) {
              lines.push(formatCustomization(opt.name, opt.price > 0 ? opt.price : null, currency));
            }
          }
        }

        if (formatNotes && item.note && item.note.trim() !== '') {
          const noteLines = formatItemNote(item.note);
          lines.push(...noteLines);
        }

        if (itemIdx < group.items.length - 1) {
          lines.push('');
        }
      }

      // TOUJOURS fermer les marqueurs
      lines.push(`[CAT:${group.categoryId}|${catName}:END]`);

      if (i < groupedItems.length - 1 && showCategories) {
        lines.push(separator);
      }
    }
  } else {
    for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
      const item = items[itemIdx];
      const itemTotal = item.menuItem.price * item.quantity;
      lines.push(formatQuantityItem(item.quantity, item.menuItem.name, itemTotal, currency));

      if (aggregateOpts) {
        for (const [, options] of Object.entries(item.customizations)) {
          const aggregated = aggregateCustomizationOptions(options);
          for (const opt of aggregated) {
            lines.push(formatCustomization(opt.name, opt.price > 0 ? opt.price : null, currency, opt.quantity));
          }
        }
      } else {
        for (const [, options] of Object.entries(item.customizations)) {
          for (const opt of options) {
            lines.push(formatCustomization(opt.name, opt.price > 0 ? opt.price : null, currency));
          }
        }
      }

      if (formatNotes && item.note && item.note.trim() !== '') {
        const noteLines = formatItemNote(item.note);
        lines.push(...noteLines);
      }

      if (itemIdx < items.length - 1) {
        lines.push('');
      }
    }
  }

  lines.push('[ITEMS:END]');
  lines.push(separator);

  lines.push('[TOTALS:START]');

  if (showTaxDetails && taxSettings?.displayMode !== 'hidden') {
    if (taxSettings?.showItemTaxDetail && taxResult && taxResult.itemDetails.length > 0) {
      lines.push(centerText(`-- ${t('Détail TVA par article')} --`));
      for (const item of taxResult.itemDetails) {
        const taxRateFormatted = item.taxRate % 1 === 0 ? item.taxRate.toFixed(0) : item.taxRate.toFixed(1);
        const taxStr = `TVA ${taxRateFormatted}%: ${item.taxAmount.toFixed(2)}${currency}`;
        lines.push(formatLine(`  ${item.name} x${item.quantity}`, taxStr));
      }
      lines.push(separator);
    }

    if (taxSettings?.displayMode === 'detailed') {
      const subtotalHT = (taxResult?.subtotalHT ?? orderTotals.subtotal).toFixed(2);
      lines.push(formatLine(`${t('Sous-total HT')}:`, `${subtotalHT}${currency}`));

      const taxAmt = (taxResult?.taxAmount ?? orderTotals.taxAmount).toFixed(2);
      if (taxResult?.taxRate !== null) {
        const taxRateFormatted = (taxResult?.taxRate ?? 0) % 1 === 0 ? (taxResult?.taxRate ?? 0).toFixed(0) : (taxResult?.taxRate ?? 0).toFixed(1);
        lines.push(formatLine(`TVA (${taxRateFormatted}%):`, `${taxAmt}${currency}`));
      } else {
        lines.push(formatLine(`TVA:`, `${taxAmt}${currency}`));
      }

      // v5 happy hour (NF525)
      if (happyHourDiscount > 0) {
        lines.push(formatLine(
          `${t('Happy Hour')} -${happyHourPercent}%:`,
          `-${happyHourDiscount.toFixed(2)}${currency}`
        ));
      }

      // v5 fidélité: ligne réduction
      if (loyaltyDiscount > 0) {
        lines.push(formatLine(
          `${t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
          `-${loyaltyDiscount.toFixed(2)}${currency}`
        ));
      }

      lines.push(separator);
      const totalTTCBefore = taxResult?.totalTTC ?? orderTotals.invoiceTotal;
      const totalTTC = Math.max(0, totalTTCBefore - loyaltyDiscount - happyHourDiscount).toFixed(2);
      if (useEscPos) {
        lines.push('\x1B\x21\x08');
      }
      lines.push(formatLine(`${t('Total TTC')}:`, `${totalTTC}${currency}`));
      if (useEscPos) {
        lines.push('\x1B\x21\x00');
      }
    } else if (taxSettings?.displayMode === 'simple') {
      const taxAmt = (taxResult?.taxAmount ?? orderTotals.taxAmount).toFixed(2);
      if (taxResult?.taxRate !== null) {
        const taxRateFormatted = (taxResult?.taxRate ?? 0) % 1 === 0 ? (taxResult?.taxRate ?? 0).toFixed(0) : (taxResult?.taxRate ?? 0).toFixed(1);
        lines.push(formatLine(`TVA (${taxRateFormatted}%):`, `${taxAmt}${currency}`));
      } else {
        lines.push(formatLine(`TVA:`, `${taxAmt}${currency}`));
      }

      // v5 happy hour (NF525)
      if (happyHourDiscount > 0) {
        lines.push(formatLine(
          `${t('Happy Hour')} -${happyHourPercent}%:`,
          `-${happyHourDiscount.toFixed(2)}${currency}`
        ));
      }

      // v5 fidélité: ligne réduction
      if (loyaltyDiscount > 0) {
        lines.push(formatLine(
          `${t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
          `-${loyaltyDiscount.toFixed(2)}${currency}`
        ));
      }

      lines.push(separator);
      const totalBefore = taxResult?.totalTTC ?? orderTotals.invoiceTotal;
      const total = Math.max(0, totalBefore - loyaltyDiscount - happyHourDiscount).toFixed(2);
      if (useEscPos) {
        lines.push('\x1B\x21\x08');
      }
      lines.push(formatLine(`${t('Total')}:`, `${total}${currency}`));
      if (useEscPos) {
        lines.push('\x1B\x21\x00');
      }
    }
  } else {
    // v5 happy hour (NF525) — affiché même en mode "hidden"
    if (happyHourDiscount > 0) {
      lines.push(formatLine(
        `${t('Happy Hour')} -${happyHourPercent}%:`,
        `-${happyHourDiscount.toFixed(2)}${currency}`
      ));
    }
    // v5 fidélité: mode "hidden" — quand même afficher la ligne réduction si applicable
    if (loyaltyDiscount > 0) {
      lines.push(formatLine(
        `${t('Reduction fidelite')} (${loyaltyPointsUsed} pts):`,
        `-${loyaltyDiscount.toFixed(2)}${currency}`
      ));
    }
    lines.push(separator);
    const totalBefore = taxResult?.totalTTC ?? orderTotals.invoiceTotal;
    const total = Math.max(0, totalBefore - loyaltyDiscount - happyHourDiscount).toFixed(2);
    if (useEscPos) {
      lines.push('\x1B\x21\x08');
    }
    lines.push(formatLine(`${t('Total')}:`, `${total}${currency}`));
    if (useEscPos) {
      lines.push('\x1B\x21\x00');
    }
  }

  lines.push('');
  lines.push(separator);
  const paymentStr = paymentMethod
    ? t(paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1))
    : t('Non spécifié');
  lines.push(formatLine(`${t('Paiement')}:`, paymentStr));
  lines.push('[TOTALS:END]');

  if (profileSettings?.siretMessage && profileSettings.siretMessage.trim() !== '') {
    lines.push(separator);
    lines.push(centerText(profileSettings.siretMessage));
  }
  if (profileSettings?.tvaMessage && profileSettings.tvaMessage.trim() !== '') {
    lines.push(separator);
    lines.push(centerText(profileSettings.tvaMessage));
  }

  lines.push(separator);
  lines.push('[FOOTER:START]');
  lines.push(centerText(t('Merci de votre visite')));
  lines.push(separator);
  lines.push('[FOOTER:END]');

  return lines.join('\n');
};
